// src/views/bots/components/BotListTour.tsx
// Guided tour for the Bot List page — auto-shows on first visit

import React, { useEffect, useState } from 'react';
import { Steps } from 'intro.js-react';
import 'intro.js/minified/introjs.min.css';

const TOUR_STORAGE_KEY = 'botListTourComplete';

const BotListTour: React.FC = () => {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem(TOUR_STORAGE_KEY) !== 'true';
    } catch {
      return true;
    }
  });

  const steps = [
    {
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 12px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            Welcome to WhatsApp Bots
          </h3>
          <p style="color: #475569; margin-bottom: 12px;">
            Create bots that <strong>automatically reply to incoming WhatsApp messages</strong> from your leads and customers.
          </p>
          <ul style="list-style: none; padding: 0; margin: 0 0 16px 0;">
            <li style="margin-bottom: 8px; display: flex; align-items: center; color: #1e293b;">
              <span style="margin-right: 8px; color: #10b981;">&#8226;</span>
              Respond instantly to pricing inquiries, product questions, support requests
            </li>
            <li style="margin-bottom: 8px; display: flex; align-items: center; color: #1e293b;">
              <span style="margin-right: 8px; color: #3b82f6;">&#8226;</span>
              Qualify leads automatically by collecting name, email, phone
            </li>
            <li style="margin-bottom: 8px; display: flex; align-items: center; color: #1e293b;">
              <span style="margin-right: 8px; color: #8b5cf6;">&#8226;</span>
              Guide customers through product catalogues, booking flows, FAQs
            </li>
          </ul>
          <p style="margin: 0; color: #64748b; font-style: italic;">Let's take a quick look around!</p>
        </div>
      `,
    },
    {
      element: '[data-tour="create-bot"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            Create a New Bot
          </h3>
          <p style="color: #475569; margin-bottom: 8px;">
            Click here to open the <strong>Visual Flow Builder</strong> — design your bot's WhatsApp conversation by dragging and connecting steps.
          </p>
          <p style="color: #64748b; font-size: 13px; margin: 0;">
            No coding needed. Build your first bot in minutes!
          </p>
        </div>
      `,
      position: 'left',
    },
    {
      element: '[data-tour="bot-search"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            Find Your Bots
          </h3>
          <p style="color: #475569;">
            Search by <strong>bot name</strong> or <strong>keyword</strong> to quickly find any bot.
          </p>
        </div>
      `,
      position: 'bottom',
    },
    {
      element: '[data-tour="bot-table"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            Your Bots
          </h3>
          <p style="color: #475569; margin-bottom: 8px;">
            Each row shows a bot with its trigger keywords, performance, and status.
          </p>
          <ul style="list-style: none; padding: 0; margin: 0;">
            <li style="margin-bottom: 6px; color: #1e293b;">
              <strong>Status toggle</strong> — Turn bots on/off. Active bots respond to WhatsApp messages instantly.
            </li>
            <li style="margin-bottom: 6px; color: #1e293b;">
              <strong>Runs</strong> — How many times the bot has been triggered by incoming messages
            </li>
            <li style="color: #1e293b;">
              <strong>Actions</strong> — Edit the flow, duplicate, or delete
            </li>
          </ul>
        </div>
      `,
      position: 'top',
    },
    {
      element: '[data-tour="bot-actions"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            Bot Actions
          </h3>
          <ul style="list-style: none; padding: 0; margin: 0;">
            <li style="margin-bottom: 6px; color: #1e293b;">
              <span style="margin-right: 6px;">&#9998;</span>
              <strong>Edit</strong> — Open the visual flow builder to modify the conversation
            </li>
            <li style="margin-bottom: 6px; color: #1e293b;">
              <span style="margin-right: 6px;">&#128203;</span>
              <strong>Copy</strong> — Duplicate the bot with all its settings (great for similar use cases)
            </li>
            <li style="color: #1e293b;">
              <span style="margin-right: 6px;">&#128465;</span>
              <strong>Delete</strong> — Permanently remove the bot
            </li>
          </ul>
        </div>
      `,
      position: 'left',
    },
  ];

  const saveTourState = (type = 'normal'): boolean => {
    try {
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
      localStorage.setItem('botListTourMetadata', JSON.stringify({
        completedAt: new Date().toISOString(),
        completionType: type,
        version: '1.0',
      }));
      return true;
    } catch {
      return false;
    }
  };

  const onExit = (): void => {
    if (saveTourState('normal')) {
      setEnabled(false);
    }
  };

  // Inject custom Gilroy styles
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'bot-list-tour-styles';
    style.textContent = `
      .introjs-tooltip {
        min-width: 380px !important;
        max-width: 480px !important;
        padding: 20px !important;
        border-radius: 12px !important;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18) !important;
        font-family: "Gilroy", system-ui, -apple-system, sans-serif !important;
      }
      .introjs-tooltiptext {
        font-size: 14px !important;
        line-height: 1.6 !important;
        color: #334155 !important;
        padding: 0 !important;
      }
      .introjs-button {
        font-family: "Gilroy", system-ui, sans-serif !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        border-radius: 8px !important;
        padding: 8px 16px !important;
        text-shadow: none !important;
        border: none !important;
        transition: all 0.15s ease !important;
      }
      .introjs-nextbutton, .introjs-donebutton {
        background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
        color: #fff !important;
      }
      .introjs-nextbutton:hover, .introjs-donebutton:hover {
        background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
      }
      .introjs-prevbutton {
        background: #f1f5f9 !important;
        color: #475569 !important;
      }
      .introjs-skipbutton {
        color: #94a3b8 !important;
        font-size: 12px !important;
      }
      .introjs-helperLayer {
        border-radius: 12px !important;
        box-shadow: 0 0 0 5000px rgba(15, 23, 42, 0.6) !important;
      }
      .introjs-bullets ul li a.active {
        background: #3b82f6 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existing = document.getElementById('bot-list-tour-styles');
      if (existing) document.head.removeChild(existing);
    };
  }, []);

  if (!enabled) return null;

  return (
    <Steps
      enabled={enabled}
      steps={steps}
      initialStep={0}
      onExit={onExit}
      options={{
        dontShowAgain: true,
        dontShowAgainLabel: "Don't show again",
        nextLabel: 'Next →',
        prevLabel: '← Back',
        doneLabel: 'Got it!',
        showStepNumbers: false,
        showBullets: true,
        showProgress: true,
        exitOnOverlayClick: false,
        exitOnEsc: true,
        scrollToElement: true,
        overlayOpacity: 0.6,
      }}
    />
  );
};

/** Reset tour so it shows again on next visit */
export const resetBotListTour = (): void => {
  try {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    localStorage.removeItem('botListTourMetadata');
  } catch { /* ignore */ }
};

export default BotListTour;
